import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ListChecks, AlertCircle, ArrowRight, MessageSquareText } from "lucide-react";
import apiClient from "../services/api";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
const FollowUpBadge = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [responses, setResponses] = useState([]);
  const [responseCount, setResponseCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const fetchFollowUps = useCallback(async () => {
    try {
      const [outRes, compRes] = await Promise.all([
        apiClient.get(`/follow-ups/outstanding`),
        apiClient.get(`/follow-ups/completion-responses`),
      ]);
      setItems(outRes.data.items || []);
      setCount(outRes.data.count || 0);
      setResponses(compRes.data.items || []);
      setResponseCount(compRes.data.count || 0);
    } catch {
      // Silent fail
    }
  }, []);

  useEffect(() => {
    fetchFollowUps();
    const interval = setInterval(fetchFollowUps, 60000);
    return () => clearInterval(interval);
  }, [fetchFollowUps]);

  const handleGoToJob = (jobId) => {
    setIsOpen(false);
    navigate(`/jobs/${jobId}?tab=notes`);
  };

  const handleReadResponse = async (resp) => {
    try {
      await apiClient.post(`/follow-ups/mark-read/${resp.job_id}/${resp.id}`);
    } catch { /* silent */ }
    handleGoToJob(resp.job_id);
    // Refresh after navigating
    setTimeout(fetchFollowUps, 500);
  };

  const totalBadge = count + responseCount;
  if (totalBadge === 0) return null;

  const overdueCount = items.filter((i) => i.overdue).length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-12 w-12 sm:h-[60px] sm:w-[60px] rounded-full"
          data-testid="followup-badge-btn"
        >
          <ListChecks className="w-[30px] h-[30px]" />
          <span
            className="absolute top-1 right-1 h-6 min-w-[24px] rounded-full bg-red-500 text-white text-sm font-bold flex items-center justify-center px-1.5"
            data-testid="followup-badge-count"
          >
            {totalBadge}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0 overflow-hidden" align="end" onWheel={(e) => e.stopPropagation()}>
        {/* Completion Responses Section */}
        {responseCount > 0 && (
          <>
            <div className="px-4 py-3 border-b bg-blue-50">
              <h3 className="font-semibold text-sm flex items-center gap-2 text-blue-800">
                <MessageSquareText className="w-4 h-4" />
                Completion Responses
                <span className="text-xs font-normal">({responseCount})</span>
              </h3>
            </div>
            <div className="max-h-40 overflow-y-auto border-b" style={{ overscrollBehavior: 'contain' }}>
              <div className="divide-y">
                {responses.map((resp) => (
                  <button
                    key={`resp-${resp.id}`}
                    onClick={() => handleReadResponse(resp)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50/50 transition-colors"
                    data-testid={`response-item-${resp.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground truncate">{resp.job_name}</p>
                        <p className="text-sm mt-0.5 truncate">{resp.content}</p>
                        <p className="text-xs text-blue-700 mt-1">
                          {resp.completion_note_by}: &ldquo;{resp.completion_note}&rdquo;
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Outstanding Follow-Ups Section */}
        {count > 0 && (
          <>
            <div className="px-4 py-3 border-b bg-muted/30">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <ListChecks className="w-4 h-4" />
                Outstanding Follow-Ups
                <span className="text-xs text-muted-foreground font-normal">({count})</span>
              </h3>
              {overdueCount > 0 && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {overdueCount} overdue
                </p>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
              <div className="divide-y">
                {items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleGoToJob(item.job_id)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
                    data-testid={`followup-item-${item.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.content}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{item.job_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {item.due_date && (
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                item.overdue
                                  ? "bg-red-100 text-red-700 font-medium"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {item.overdue ? "Overdue: " : "Due: "}{item.due_date}
                            </span>
                          )}
                          {item.assigned_to && (
                            <span className="text-xs text-muted-foreground">
                              {item.assigned_to}
                            </span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default FollowUpBadge;
